import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { apiRequest } from '../../utils/apiHelper';
import { MagnifyingGlassIcon, ArrowPathIcon, BriefcaseIcon } from '@heroicons/react/24/outline';

const CaseResearch = () => {
  const [query, setQuery] = useState('');
  const [jurisdiction] = useState('zimbabwe'); // Default to Zimbabwe
  const [selectedCase, setSelectedCase] = useState('');
  const [cases, setCases] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingCases, setLoadingCases] = useState(true);
  const location = useLocation();

  // Placeholder for future jurisdiction selection
  // const jurisdictions = [
  //   { id: 'zimbabwe', name: 'Zimbabwe' },
  // ];

  // Fetch user's cases on component mount
  useEffect(() => {
    const fetchCases = async () => {
      try {
        setLoadingCases(true);
        const response = await apiRequest('/api/cases/');
        // Ensure response is always an array
        setCases(Array.isArray(response) ? response : []);
        
        // If we have a case_id in the location state, select it
        if (location.state && location.state.caseId) {
          setSelectedCase(location.state.caseId.toString());
        }
      } catch (err) {
        console.error('Failed to fetch cases:', err);
        setCases([]); // Set empty array on error
      } finally {
        setLoadingCases(false);
      }
    };

    fetchCases();
  }, [location]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    // Require case selection for case-based research
    if (!selectedCase) {
      setError('Please select a case to perform case-based research.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const requestData = {
        query: query.trim(),
        jurisdiction: jurisdiction || 'zimbabwe', // Default to Zimbabwe, allow override
        case_id: selectedCase
      };
      
      const response = await apiRequest('/api/research/perform-search/', 'POST', requestData);

      // Handle response format - extract results array
      if (response && response.results) {
        setResults(response.results);
      } else if (Array.isArray(response)) {
        setResults(response);
      } else {
        setResults([]);
      }
    } catch (err) {
      console.error('Case research query failed:', err);
      setError('Failed to perform research. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="case-research">
      <div className="mb-6">
        <div className="bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <BriefcaseIcon className="h-5 w-5 text-blue-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                Case-Based Research — Multi-Jurisdiction
              </h3>
              <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                <p>
                  Perform legal research within the context of a specific case across African jurisdictions.
                  Select a case to provide research context. Results include jurisdiction, source, and date accessed.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSearch} className="mb-6">
        <div className="flex flex-col space-y-4">
          <div>
            <label htmlFor="case-select" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Select Case <span className="text-red-500">*</span>
            </label>
            <div className="mt-1">
              <select
                id="case-select"
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                value={selectedCase}
                onChange={(e) => setSelectedCase(e.target.value)}
                disabled={loadingCases}
              >
                <option value="">Select a case</option>
                {(cases || []).map((caseItem) => (
                  <option key={caseItem.id} value={caseItem.id}>
                    {caseItem.title}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="research-query" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Research Query
            </label>
            <div className="mt-1">
              <textarea
                id="research-query"
                rows={3}
                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                placeholder="Enter your legal research question..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                required
              />
            </div>
          </div>
          
          <div>
            <label htmlFor="jurisdiction" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Jurisdiction
            </label>
            <div className="mt-1">
              <input
                type="text"
                id="jurisdiction"
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white bg-gray-50 dark:bg-gray-600 cursor-not-allowed sm:text-sm rounded-md"
                value="Zimbabwe"
                readOnly
                disabled
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Case-based research is focused on Zimbabwe jurisdiction only
              </p>
            </div>
          </div>
          
          <div>
            <button
              type="submit"
              disabled={loading || !query.trim()}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <ArrowPathIcon className="animate-spin -ml-1 mr-2 h-5 w-5" />
                  Searching...
                </>
              ) : (
                <>
                  <MagnifyingGlassIcon className="-ml-1 mr-2 h-5 w-5" />
                  Research Case in Zimbabwe
                </>
              )}
            </button>
          </div>
        </div>
      </form>

      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {results.length > 0 && (
        <div className="results-container">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Research Results</h2>
          {selectedCase && (
            <div className="mb-4 py-2 px-4 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 dark:border-blue-700 rounded-md">
              <div className="flex">
                <BriefcaseIcon className="h-5 w-5 text-blue-500 dark:text-blue-400 mr-2" />
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Results are associated with case: {cases.find(c => c.id === parseInt(selectedCase))?.title || selectedCase}
                </p>
              </div>
            </div>
          )}
          <div className="overflow-hidden bg-white dark:bg-gray-800 shadow sm:rounded-md">
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {results.map((result, index) => (
                <li key={index} className="px-3 py-4 sm:px-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <h3 className="text-sm sm:text-base font-medium text-primary-600 dark:text-primary-400 break-words min-w-0">{result.title}</h3>
                    <p className="self-start sm:self-auto px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 whitespace-nowrap">
                      {(result.relevance_score * 100).toFixed(0)}% relevance
                    </p>
                  </div>
                  <div className="mt-2 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    <p className="line-clamp-3 sm:line-clamp-2">{result.excerpt}</p>
                  </div>
                  {result.source && (
                    <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      Source: {result.source}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {!loading && !error && query && results.length === 0 && (
        <div className="text-center py-12">
          <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No results found</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Try adjusting your search terms or changing the jurisdiction.
          </p>
        </div>
      )}
    </div>
  );
};

export default CaseResearch;
